import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = [
  "Claim query",
  "Policy/coverage question",
  "E-card request",
  "Network hospital request",
  "Grievance/complaint",
  "Other",
];

export function TicketForm({ onSubmitted }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("Claim query");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [email, setEmail] = useState(user?.email || "");
  const [serviceReason, setServiceReason] = useState("Cashless");
  const [claimNumber, setClaimNumber] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!mobile.trim()) { toast.error("Please enter your mobile number."); return; }
    if (mobile.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    if (!email.trim()) { toast.error("Please enter your email ID."); return; }
    if (!description.trim()) { toast.error("Please describe your issue."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/tickets", {
        category,
        description: description.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        service_reason: serviceReason,
        claim_number: claimNumber.trim(),
      });
      setTicket(data);
      toast.success(`Ticket ${data.ticket_number} raised!`);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (ticket) {
    return (
      <div data-testid="ticket-form-done" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        🎫 Ticket <b>{ticket.ticket_number}</b> raised! Our support team will follow up soon. You can track it under "My tickets".
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="ticket-form"
      className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-3">
      <p className="font-heading font-semibold text-emerald-950">Raise a service ticket</p>

      <div className="space-y-1.5">
        <Label className="text-xs">Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Mobile number</Label>
          <Input data-testid="ticket-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9886260579" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Email ID</Label>
          <Input data-testid="ticket-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Service reason</Label>
        <Select value={serviceReason} onValueChange={setServiceReason}>
          <SelectTrigger data-testid="ticket-service-reason"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Cashless">Cashless</SelectItem>
            <SelectItem value="Reimbursement">Reimbursement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Claim number <span className="text-zinc-400">(optional)</span></Label>
        <Input data-testid="ticket-claim-number" value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} placeholder="CLM-123456" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Describe your issue</Label>
        <Textarea data-testid="ticket-description" rows={3} value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Tell us what you need help with…" />
      </div>

      <Button type="submit" disabled={loading} data-testid="ticket-submit"
        className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
        {loading ? "Raising…" : "Raise Ticket"}
      </Button>
    </form>
  );
}
